import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LogOut,
  User,
  LayoutDashboard,
  LogIn,
  Menu,
  X,
} from "lucide-react";

type SectionId = "live-showcase" | "what-is-tool" | "faq-section";

const scrollToSection = (id: SectionId) => {
  const el = document.getElementById(id);
  if (el) {
    const yOffset = -80;
    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

export default function NavBar() {
  const { token, user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (location.hash) {
      const section = location.hash.replace("#", "") as SectionId;
      setTimeout(() => scrollToSection(section), 150);
    }
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleNavClick = (id: SectionId) => {
    setMenuOpen(false);
    if (location.pathname === "/") {
      scrollToSection(id);
    } else {
      navigate(`/#${id}`);
    }
  };

  useEffect(() => {
    if (token) refreshUser();
  }, [token, refreshUser]);

  return (
    <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
        {/* ---------- Logo ---------- */}
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate("/")}
        >
          <img
            src="/logo.png"
            alt="Logo"
            className="w-24 sm:w-28 h-auto object-contain"
          />
        </div>

        {/* ---------- Desktop Menu ---------- */}
        <div className="hidden sm:flex items-center gap-8">
          {!token && (
            <>
              <button
                onClick={() => handleNavClick("live-showcase")}
                className="text-gray-700 font-medium hover:text-blue-600 transition-colors"
              >
                Features
              </button>
              <button
                onClick={() => handleNavClick("what-is-tool")}
                className="text-gray-700 font-medium hover:text-blue-600 transition-colors"
              >
                Why Us
              </button>
              <button
                onClick={() => handleNavClick("faq-section")}
                className="text-gray-700 font-medium hover:text-blue-600 transition-colors"
              >
                FAQ
              </button>
              <Link
                to="/pricing"
                className="text-gray-700 font-medium hover:text-blue-600 transition-colors"
              >
                Pricing
              </Link>
            </>
          )}

          {token && user ? (
            <>
              <Link
                to="/dashboard"
                className="hover:text-blue-600 flex items-center gap-1 transition-colors"
              >
                <LayoutDashboard className="w-5 h-5" />
                Dashboard
              </Link>
              <Link
                to="/profile"
                className="hover:text-blue-600 flex items-center gap-1 transition-colors"
              >
                <User className="w-5 h-5" />
                Profile
              </Link>
              <span className="text-sm text-gray-600">
                Credits:{" "}
                <span className="text-green-600 font-semibold">
                  {user.credits}
                </span>
              </span>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-all flex items-center gap-1"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={() =>
                (window.location.href = `${process.env.REACT_APP_BACKEND_URL}/auth/login`)
              }
              className="bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-all flex items-center gap-1 shadow-sm"
            >
              <LogIn className="w-5 h-5" />
              Login with Google
            </button>
          )}
        </div>

        {/* ---------- Mobile Hamburger ---------- */}
        <div className="sm:hidden">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-blue-700 focus:outline-none"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* ---------- Mobile Dropdown ---------- */}
      {menuOpen && (
        <div className="sm:hidden bg-white border-t border-gray-100 shadow-inner">
          <div className="flex flex-col px-5 py-4 gap-4">
            {!token && (
              <>
                <button
                  onClick={() => handleNavClick("live-showcase")}
                  className="text-gray-700 font-medium text-left hover:text-blue-600"
                >
                  Features
                </button>
                <button
                  onClick={() => handleNavClick("what-is-tool")}
                  className="text-gray-700 font-medium text-left hover:text-blue-600"
                >
                  Why Us
                </button>
                <button
                  onClick={() => handleNavClick("faq-section")}
                  className="text-gray-700 font-medium text-left hover:text-blue-600"
                >
                  FAQ
                </button>
                <Link
                  to="/pricing"
                  className="text-gray-700 font-medium hover:text-blue-600"
                  onClick={() => setMenuOpen(false)}
                >
                  Pricing
                </Link>
              </>
            )}

            {token && user ? (
              <>
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 text-gray-700 hover:text-blue-600"
                  onClick={() => setMenuOpen(false)}
                >
                  <LayoutDashboard className="w-5 h-5" />
                  Dashboard
                </Link>
                <Link
                  to="/profile"
                  className="flex items-center gap-2 text-gray-700 hover:text-blue-600"
                  onClick={() => setMenuOpen(false)}
                >
                  <User className="w-5 h-5" />
                  Profile
                </Link>
                <span className="text-sm text-gray-600">
                  Credits:{" "}
                  <span className="text-green-600 font-semibold">
                    {user.credits}
                  </span>
                </span>
                <button
                  onClick={() => {
                    handleLogout();
                    setMenuOpen(false);
                  }}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-1 hover:bg-red-700 transition-all"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            ) : (
              <button
                onClick={() =>
                  (window.location.href = `${process.env.REACT_APP_BACKEND_URL}/auth/login`)
                }
                className="bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-all flex items-center gap-1"
              >
                <LogIn className="w-5 h-5" />
                Login with Google
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
